import {
  LocationWithScrollDowns,
  LocationModal,
} from "models/wizard/wizard-modals";
import React, { useEffect, useState } from "react";
import { PiCameraRotateLight, PiGenderMale, PiGenderFemale } from "react-icons/pi";
import { LiaSearchLocationSolid } from "react-icons/lia";
import Body, { ExtendedBodyPart, Slug } from "components/react-body-highlighter";

export interface SubLocationData {
  label: string;
  sublocations: string[];
}

export interface BodyLocationsData {
  male: {
    front: Record<string, SubLocationData>;
    back: Record<string, SubLocationData>;
  };
  female: {
    front: Record<string, SubLocationData>;
    back: Record<string, SubLocationData>;
  };
  front: Record<string, SubLocationData>;
  back: Record<string, SubLocationData>;
}

const MALE_BODY_LOCATIONS = {
  front: {
    head: {
      label: "Head",
      sublocations: ["Forehead", "Scalp", "Crown", "Temporal Region", "Face", "Eyes", "Nose", "Cheeks", "Jaw", "Chin", "Mouth"],
    },
    hair: {
      label: "Hair",
      sublocations: ["Hairline", "Scalp Top", "Temporal Hair"],
    },
    neck: {
      label: "Neck",
      sublocations: ["Anterior Neck", "Lateral Neck", "Sternocleidomastoid", "Thyroid Region"],
    },
    trapezius: {
      label: "Trapezius (Front)",
      sublocations: ["Clavicular Region", "Supraclavicular", "Anterior Shoulder Junction"],
    },
    deltoids: {
      label: "Shoulder (Deltoid)",
      sublocations: ["Anterior Deltoid", "Lateral Deltoid", "Clavicular Head", "Acromion"],
    },
    chest: {
      label: "Chest (Pectorals)",
      sublocations: ["Upper Pectoralis", "Lower Pectoralis", "Infraclavicular", "Inframammary Fold", "Sternum", "Areola Region", "Sternal Border", "Clavipectoral Triangle"],
    },
    biceps: {
      label: "Biceps",
      sublocations: ["Upper Biceps", "Lower Biceps", "Biceps Short Head", "Biceps Long Head"],
    },
    triceps: {
      label: "Triceps (Front)",
      sublocations: ["Medial Triceps", "Lateral Triceps", "Posterior Arm"],
    },
    forearm: {
      label: "Forearm",
      sublocations: ["Anterior Forearm", "Medial Forearm", "Lateral Forearm", "Wrist"],
    },
    hands: {
      label: "Hand",
      sublocations: ["Palm", "Fingers", "Thumb", "Thenar Eminence", "Wrist"],
    },
    abs: {
      label: "Abdomen (Abs)",
      sublocations: ["Upper Rectus Abdominis", "Lower Rectus Abdominis", "Epigastric Region", "Umbilical Region", "Hypogastric Region", "Suprapubic", "Iliac Fossa"],
    },
    obliques: {
      label: "Obliques",
      sublocations: ["Upper Oblique", "Lower Oblique", "Flank", "Serratus Anterior", "Lateral Abdomen"],
    },
    adductors: {
      label: "Adductors (Inner Thigh)",
      sublocations: ["Inner Thigh", "Gracilis", "Pectineus", "Iliopsoas", "Inguinal", "Tensor Fasciae Latae"],
    },
    quadriceps: {
      label: "Quadriceps",
      sublocations: ["Rectus Femoris", "Vastus Lateralis", "Vastus Medialis", "Upper Thigh", "Sartorius"],
    },
    knees: {
      label: "Knee",
      sublocations: ["Patella", "Medial Knee", "Lateral Knee", "Subpatellar", "Tibial Tuberosity"],
    },
    tibialis: {
      label: "Shin (Tibialis Anterior)",
      sublocations: ["Anterior Shin", "Lateral Shin", "Fibula Region"],
    },
    calves: {
      label: "Calves (Front)",
      sublocations: ["Anterior Lower Leg", "Lateral Lower Leg", "Fibula Region"],
    },
    ankles: {
      label: "Ankle",
      sublocations: ["Medial Malleolus", "Lateral Malleolus", "Anterior Ankle", "Ankle Joint"],
    },
    feet: {
      label: "Foot",
      sublocations: ["Dorsum Foot", "Toes", "Plantar Fascia", "Arch", "Ball of Foot"],
    },
    groin: {
      label: "Male Genital Area",
      sublocations: ["Penis", "Scrotum / Testicular", "Penile Shaft", "Penile Glans", "Pubic Region", "Inguinal Region", "Perineum", "Suprapubic Region"],
    },
  },
  back: {
    hair: {
      label: "Hair (Posterior)",
      sublocations: ["Occipital Hairline", "Posterior Crown"],
    },
    head: {
      label: "Head (Posterior)",
      sublocations: ["Occipital Region", "Posterior Crown", "Base of Skull"],
    },
    neck: {
      label: "Neck (Posterior)",
      sublocations: ["Suboccipital Region", "Upper Cervical Spine", "Nucha", "Nape"],
    },
    trapezius: {
      label: "Trapezius",
      sublocations: ["Upper Trapezius", "Mid Trapezius", "Lower Trapezius", "Neck-Shoulder Junction", "Interscapular"],
    },
    deltoids: {
      label: "Rear Deltoid",
      sublocations: ["Posterior Deltoid", "Infraspinatus", "Posterior Axilla", "Teres Minor"],
    },
    "upper-back": {
      label: "Upper Back (Lats)",
      sublocations: ["Upper Latissimus", "Mid Latissimus", "Lower Latissimus", "Rhomboids", "Thoracic Spine"],
    },
    triceps: {
      label: "Triceps",
      sublocations: ["Triceps Long Head", "Triceps Lateral Head", "Triceps Medial Head", "Posterior Arm"],
    },
    forearm: {
      label: "Forearm (Posterior)",
      sublocations: ["Posteromedial Forearm", "Posterolateral Forearm", "Extensor Digitorum", "Wrist Back"],
    },
    hands: {
      label: "Hand (Posterior)",
      sublocations: ["Dorsum Hand", "Dorsal Fingers", "Wrist Back", "Knuckles"],
    },
    "lower-back": {
      label: "Lower Back",
      sublocations: ["Erector Spinae Lumbar", "Paravertebral", "Quadratus Lumborum", "Flank Posterior", "Lumbar Spine", "Sacrum"],
    },
    adductors: {
      label: "Adductors (Posterior)",
      sublocations: ["Posterior Inner Thigh", "Gracilis Posterior", "Semimembranosus"],
    },
    gluteal: {
      label: "Gluteal",
      sublocations: ["Gluteus Maximus", "Gluteus Medius", "Upper Buttock", "Lower Gluteal Fold", "Lateral Hip Posterior"],
    },
    "gluteal-crease": {
      label: "Gluteal Crease",
      sublocations: ["Gluteal Fold", "Inferior Gluteal", "Perineal Region"],
    },
    hamstring: {
      label: "Hamstrings",
      sublocations: ["Biceps Femoris", "Semimembranosus", "Semitendinosus", "Inner Thigh Back", "Outer Thigh Back"],
    },
    calves: {
      label: "Calves",
      sublocations: ["Gastrocnemius Medial", "Gastrocnemius Lateral", "Soleus", "Lower Calf", "Achilles Region"],
    },
    ankles: {
      label: "Ankle (Posterior)",
      sublocations: ["Achilles Tendon", "Posterior Ankle", "Achilles Tendon Insertion"],
    },
    feet: {
      label: "Foot (Posterior)",
      sublocations: ["Heel", "Plantar Aspect", "Achilles Tendon Insertion", "Sole"],
    },
    perineum: {
      label: "Perineum / Perianal Area (Male)",
      sublocations: [
        "Perineal Body",
        "Perineal Raphe",
        "Urogenital Triangle",
        "Anal Triangle",
        "Perianal Region",
        "Anal Sphincter Region",
        "Anal Verge",
        "Coccyx",
      ],
    },
    backbone: {
      label: "Backbone (Spine)",
      sublocations: [
        "Cervical Spine",
        "Thoracic Spine",
        "Lumbar Spine",
        "Sacrum",
        "Coccyx",
        "Vertebrae",
        "Intervertebral Discs",
        "Paraspinal Region",
      ],
    },
  },
};

const FEMALE_BODY_LOCATIONS = {
  front: {
    head: {
      label: "Head",
      sublocations: ["Forehead", "Scalp", "Crown", "Temporal Region", "Face", "Eyes", "Nose", "Cheeks", "Jaw", "Chin", "Mouth"],
    },
    hair: {
      label: "Hair",
      sublocations: ["Hairline", "Scalp Top", "Temporal Hair"],
    },
    neck: {
      label: "Neck",
      sublocations: ["Anterior Neck", "Lateral Neck", "Sternocleidomastoid", "Thyroid Region"],
    },
    trapezius: {
      label: "Trapezius (Front)",
      sublocations: ["Clavicular Region", "Supraclavicular", "Anterior Shoulder Junction"],
    },
    deltoids: {
      label: "Shoulder (Deltoid)",
      sublocations: ["Anterior Deltoid", "Lateral Deltoid", "Clavicular Head", "Acromion"],
    },
    breasts: {
      label: "Breasts / Chest",
      sublocations: ["Upper Outer Quadrant", "Upper Inner Quadrant", "Lower Outer Quadrant", "Lower Inner Quadrant", "Areola / Nipple", "Upper Breasts", "Lower Breasts", "Infraclavicular", "Inframammary Fold", "Axillary Tail of Spence"],
    },
    biceps: {
      label: "Biceps",
      sublocations: ["Upper Biceps", "Lower Biceps", "Biceps Short Head", "Biceps Long Head"],
    },
    triceps: {
      label: "Triceps (Front)",
      sublocations: ["Medial Triceps", "Lateral Triceps", "Posterior Arm"],
    },
    forearm: {
      label: "Forearm",
      sublocations: ["Anterior Forearm", "Medial Forearm", "Lateral Forearm", "Wrist"],
    },
    hands: {
      label: "Hand",
      sublocations: ["Palm", "Fingers", "Thumb", "Thenar Eminence", "Wrist"],
    },
    "female-abs": {
      label: "Abdomen / Pelvic Area",
      sublocations: ["Upper Rectus Abdominis", "Lower Rectus Abdominis", "Epigastric Region", "Umbilical Region", "Hypogastric Region", "Suprapubic / Pelvic Floor", "Iliac Fossa", "Lateral Abdomen"],
    },
    obliques: {
      label: "Obliques",
      sublocations: ["Upper Oblique", "Lower Oblique", "Flank", "Serratus Anterior", "Lateral Abdomen"],
    },
    adductors: {
      label: "Adductors (Inner Thigh)",
      sublocations: ["Inner Thigh", "Gracilis", "Pectineus", "Iliopsoas", "Inguinal", "Tensor Fasciae Latae"],
    },
    quadriceps: {
      label: "Quadriceps",
      sublocations: ["Rectus Femoris", "Vastus Lateralis", "Vastus Medialis", "Upper Thigh", "Sartorius"],
    },
    knees: {
      label: "Knee",
      sublocations: ["Patella", "Medial Knee", "Lateral Knee", "Subpatellar", "Tibial Tuberosity"],
    },
    tibialis: {
      label: "Shin (Tibialis Anterior)",
      sublocations: ["Anterior Shin", "Lateral Shin", "Fibula Region"],
    },
    calves: {
      label: "Calves (Front)",
      sublocations: ["Anterior Lower Leg", "Lateral Lower Leg", "Fibula Region"],
    },
    ankles: {
      label: "Ankle",
      sublocations: ["Medial Malleolus", "Lateral Malleolus", "Anterior Ankle", "Ankle Joint"],
    },
    feet: {
      label: "Foot",
      sublocations: ["Dorsum Foot", "Toes", "Plantar Fascia", "Arch", "Ball of Foot"],
    },
    "female-vagina": {
      label: "Female Genital Area",
      sublocations: ["Vagina", "Clitoris", "Labia Majora", "Labia Minora", "Mons Pubis", "Vaginal Introitus", "Vulva", "Perineum", "Pubic Region", "Inguinal Region", "Urethral Meatus"],
    },
    vagina: {
      label: "Female Genital Area",
      sublocations: ["Vagina", "Clitoris", "Labia Majora", "Labia Minora", "Mons Pubis", "Vaginal Introitus", "Vulva", "Perineum", "Pubic Region", "Inguinal Region", "Urethral Meatus"],
    },
  },
  back: {
    hair: {
      label: "Hair (Posterior)",
      sublocations: ["Occipital Hairline", "Posterior Crown"],
    },
    head: {
      label: "Head (Posterior)",
      sublocations: ["Occipital Region", "Posterior Crown", "Base of Skull"],
    },
    neck: {
      label: "Neck (Posterior)",
      sublocations: ["Suboccipital Region", "Upper Cervical Spine", "Nucha", "Nape"],
    },
    trapezius: {
      label: "Trapezius",
      sublocations: ["Upper Trapezius", "Mid Trapezius", "Lower Trapezius", "Neck-Shoulder Junction", "Interscapular"],
    },
    deltoids: {
      label: "Rear Deltoid",
      sublocations: ["Posterior Deltoid", "Infraspinatus", "Posterior Axilla", "Teres Minor"],
    },
    "upper-back": {
      label: "Upper Back (Lats)",
      sublocations: ["Upper Latissimus", "Mid Latissimus", "Lower Latissimus", "Rhomboids", "Thoracic Spine"],
    },
    triceps: {
      label: "Triceps",
      sublocations: ["Triceps Long Head", "Triceps Lateral Head", "Triceps Medial Head", "Posterior Arm"],
    },
    forearm: {
      label: "Forearm (Posterior)",
      sublocations: ["Posteromedial Forearm", "Posterolateral Forearm", "Extensor Digitorum", "Wrist Back"],
    },
    hands: {
      label: "Hand (Posterior)",
      sublocations: ["Dorsum Hand", "Dorsal Fingers", "Wrist Back", "Knuckles"],
    },
    "lower-back": {
      label: "Lower Back",
      sublocations: ["Erector Spinae Lumbar", "Paravertebral", "Quadratus Lumborum", "Flank Posterior", "Lumbar Spine", "Sacrum"],
    },
    gluteal: {
      label: "Gluteal",
      sublocations: ["Gluteus Maximus", "Gluteus Medius", "Upper Buttock", "Lower Gluteal Fold", "Lateral Hip Posterior"],
    },
    "female-gluteal-crease": {
      label: "Gluteal Crease / Cleft",
      sublocations: ["Intergluteal Cleft", "Upper Gluteal Fold", "Sacrococcygeal Region", "Inferior Gluteal Fold"],
    },
    adductors: {
      label: "Adductors (Posterior)",
      sublocations: ["Posterior Inner Thigh", "Gracilis Posterior", "Semimembranosus"],
    },
    hamstring: {
      label: "Hamstrings",
      sublocations: ["Biceps Femoris", "Semimembranosus", "Semitendinosus", "Inner Thigh Back", "Outer Thigh Back"],
    },
    calves: {
      label: "Calves",
      sublocations: ["Gastrocnemius Medial", "Gastrocnemius Lateral", "Soleus", "Lower Calf", "Achilles Region"],
    },
    ankles: {
      label: "Ankle (Posterior)",
      sublocations: ["Achilles Tendon", "Posterior Ankle", "Achilles Tendon Insertion"],
    },
    feet: {
      label: "Foot (Posterior)",
      sublocations: ["Heel", "Plantar Aspect", "Achilles Tendon Insertion", "Sole"],
    },
    "female-perineum": {
      label: "Perineum / Perianal Area (Female)",
      sublocations: [
        "Perineal Body",
        "Posterior Fourchette",
        "Urogenital Triangle",
        "Anal Triangle",
        "Perianal Region",
        "Anal Sphincter Region",
        "Anal Verge",
        "Coccyx",
      ],
    },
    backbone: {
      label: "Backbone (Spine)",
      sublocations: [
        "Cervical Spine",
        "Thoracic Spine",
        "Lumbar Spine",
        "Sacrum",
        "Coccyx",
        "Vertebrae",
        "Intervertebral Discs",
        "Paraspinal Region",
      ],
    },
  },
};

/**
 * BODY_LOCATIONS_DATA provides default dataset for male and female body models.
 */
export const BODY_LOCATIONS_DATA: BodyLocationsData = {
  male: MALE_BODY_LOCATIONS,
  female: FEMALE_BODY_LOCATIONS,
  front: MALE_BODY_LOCATIONS.front,
  back: MALE_BODY_LOCATIONS.back,
};

/** Body parts that have distinct left/right sides in the package */
export const BILATERAL_SLUGS: Slug[] = [
  "neck",
  "trapezius",
  "deltoids",
  "chest",
  "breasts",
  "biceps",
  "triceps",
  "forearm",
  "hands",
  "abs",
  "female-abs",
  "obliques",
  "adductors",
  "quadriceps",
  "knees",
  "tibialis",
  "calves",
  "ankles",
  "feet",
  "upper-back",
  "lower-back",
  "gluteal",
  "hamstring",
];

export interface BodyHighlighterPart extends ExtendedBodyPart {
  bodySide?: "front" | "back";
}

/**
 * Given a slug, side, viewSide ("Front" | "Back") and gender, returns the display label.
 */
export const buildLabel = (
  slug: Slug,
  side: "left" | "right" | undefined,
  locationList: LocationModal[] | undefined,
  viewSide: "Front" | "Back",
  gender: "male" | "female" = "male"
): string => {
  let effectiveSlug = slug;
  if (effectiveSlug === ("anus" as Slug)) effectiveSlug = "perineum";
  if (effectiveSlug === ("female-anus" as Slug)) effectiveSlug = "female-perineum";
  if (effectiveSlug === ("spine" as Slug)) effectiveSlug = "backbone";

  const isBilateral = BILATERAL_SLUGS.includes(effectiveSlug);
  const effectiveSide = isBilateral ? side : undefined;
  const prefix = effectiveSide === "left" ? "Left" : effectiveSide === "right" ? "Right" : "";
  const sideKey = viewSide.toLowerCase() as "front" | "back";

  // 1. DB lookup isolated by gender AND view side (Front / Back)
  if (Array.isArray(locationList) && locationList.length > 0) {
    const sideGenderKey = effectiveSide
      ? `${gender}-${sideKey}-${effectiveSlug}-${effectiveSide}`
      : `${gender}-${sideKey}-${effectiveSlug}`;
    const sideBaseKey = effectiveSide ? `${sideKey}-${effectiveSlug}-${effectiveSide}` : `${sideKey}-${effectiveSlug}`;
    const legacyGenderKey = effectiveSide ? `${gender}-${effectiveSlug}-${effectiveSide}` : `${gender}-${effectiveSlug}`;
    const legacyBaseKey = effectiveSide ? `${effectiveSlug}-${effectiveSide}` : effectiveSlug;

    // Helper to filter out opposite gender records
    const isOpposite = (loc: LocationModal) => {
      if (gender === "female" && (loc.gender === "male" || loc.muscle_id?.startsWith("male-"))) return true;
      if (gender === "male" && (loc.gender === "female" || loc.muscle_id?.startsWith("female-"))) return true;
      const d = (loc.description || "").toLowerCase();
      if (gender === "female" && d.includes("male") && !d.includes("female")) return true;
      if (gender === "male" && d.includes("female")) return true;
      return false;
    };

    // Helper to check if record belongs to opposite body view side (front vs back)
    const isOppositeSide = (loc: LocationModal) => {
      if (loc.side && loc.side !== sideKey) return true;
      if (loc.muscle_id) {
        if (sideKey === "front" && (loc.muscle_id.includes("-back-") || loc.muscle_id.startsWith("back-"))) return true;
        if (sideKey === "back" && (loc.muscle_id.includes("-front-") || loc.muscle_id.startsWith("front-"))) return true;
      }
      const targetGenderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
      const oppositeSideKey = sideKey === "front" ? "back" : "front";
      const descLower = (loc.description || "").toLowerCase();
      const strippedDesc = descLower.replace(/^(left|right)\s+/, "");

      const oppositeMatch = Object.values(targetGenderData[oppositeSideKey]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );
      const currentMatch = Object.values(targetGenderData[sideKey]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );

      if (oppositeMatch && !currentMatch) return true;

      if (sideKey === "back" && !loc.side && (!loc.muscle_id || (!loc.muscle_id.includes("-back-") && !loc.muscle_id.startsWith("back-")))) {
        const isFrontLabel = Object.values(targetGenderData.front).some(
          (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
        );
        if (isFrontLabel) return true;
      }

      return false;
    };

    const dbMatch =
      // 1a. Direct gender+side-prefixed muscle_id match (e.g. "male-front-biceps-left" or "female-back-head")
      locationList.find((loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === sideGenderKey) ||
      // 1b. Direct sideBaseKey match with gender
      locationList.find(
        (loc) => !isOpposite(loc) && loc.gender === gender && loc.muscle_id === sideBaseKey
      ) ||
      // 1c. Explicit gender + side match with muscle_id
      locationList.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          loc.gender === gender &&
          (loc.muscle_id === sideGenderKey || loc.muscle_id === sideBaseKey)
      ) ||
      // 1d. Match by default label for this exact gender and side
      (() => {
        const defaultSideLabel = (gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male)[sideKey]?.[slug]?.label;
        if (!defaultSideLabel) return undefined;
        const targetDesc = prefix ? `${prefix} ${defaultSideLabel}` : defaultSideLabel;
        return locationList.find(
          (loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.description.toLowerCase() === targetDesc.toLowerCase()
        );
      })() ||
      // 1e. Legacy matches (only if no opposite side indicated)
      locationList.find((loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === legacyGenderKey) ||
      locationList.find((loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === legacyBaseKey) ||
      locationList.find((loc) => !isOpposite(loc) && !isOppositeSide(loc) && loc.muscle_id === slug);

    if (dbMatch?.description) {
      if (prefix && !dbMatch.description.startsWith("Left ") && !dbMatch.description.startsWith("Right ")) {
        return `${prefix} ${dbMatch.description}`;
      }
      return dbMatch.description;
    }
  }

  // 2. Default data fallback isolated strictly to the selected gender and side
  const genderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
  const defaultInfo =
    genderData[sideKey]?.[slug] ||
    genderData.front[slug] ||
    genderData.back[slug];

  const baseLabel = defaultInfo?.label || slug;

  if (prefix) {
    return `${prefix} ${baseLabel}`;
  }
  return baseLabel;
};

export const getSublocationsForLabel = (
  label: string,
  locationList: LocationModal[] | undefined,
  gender: "male" | "female" = "male",
  viewSide?: "Front" | "Back"
): string[] => {
  const stripped = label.replace(/^(Left|Right)\s/, "");
  const sideKey = viewSide ? (viewSide.toLowerCase() as "front" | "back") : undefined;

  // 1. DB lookup isolated strictly to this gender and view side
  if (Array.isArray(locationList) && locationList.length > 0) {
    const isOpposite = (loc: LocationModal) => {
      if (gender === "female" && (loc.gender === "male" || loc.muscle_id?.startsWith("male-"))) return true;
      if (gender === "male" && (loc.gender === "female" || loc.muscle_id?.startsWith("female-"))) return true;
      const d = (loc.description || "").toLowerCase();
      if (gender === "female" && d.includes("male") && !d.includes("female")) return true;
      if (gender === "male" && d.includes("female")) return true;
      return false;
    };

    const isOppositeSide = (loc: LocationModal) => {
      if (!sideKey) return false;
      if (loc.side && loc.side !== sideKey) return true;
      if (loc.muscle_id) {
        if (sideKey === "front" && (loc.muscle_id.includes("-back-") || loc.muscle_id.startsWith("back-"))) return true;
        if (sideKey === "back" && (loc.muscle_id.includes("-front-") || loc.muscle_id.startsWith("front-"))) return true;
      }
      const targetGenderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
      const oppositeSideKey = sideKey === "front" ? "back" : "front";
      const descLower = (loc.description || "").toLowerCase();
      const strippedDesc = descLower.replace(/^(left|right)\s+/, "");

      const oppositeMatch = Object.values(targetGenderData[oppositeSideKey]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );
      const currentMatch = Object.values(targetGenderData[sideKey]).some(
        (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
      );

      if (oppositeMatch && !currentMatch) return true;

      if (sideKey === "back" && !loc.side && (!loc.muscle_id || (!loc.muscle_id.includes("-back-") && !loc.muscle_id.startsWith("back-")))) {
        const isFrontLabel = Object.values(targetGenderData.front).some(
          (v) => v.label.toLowerCase() === descLower || v.label.toLowerCase() === strippedDesc
        );
        if (isFrontLabel) return true;
      }

      return false;
    };

    // First try to match records of the target gender and description
    const dbMatch =
      locationList.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          (loc.description === label || loc.description === stripped)
      ) ||
      locationList.find(
        (loc) =>
          !isOpposite(loc) &&
          !isOppositeSide(loc) &&
          (loc.gender === gender || loc.muscle_id?.startsWith(`${gender}-`)) &&
          (loc.muscle_id === label ||
            loc.muscle_id === stripped ||
            (sideKey && (loc.muscle_id === `${gender}-${sideKey}-${stripped}` || loc.muscle_id === `${gender}-${sideKey}-${label}`)))
      ) ||
      locationList.find(
        (loc) =>
          !isOpposite(loc) &&
          (loc.description === label || loc.description === stripped)
      );

    if (dbMatch && Array.isArray(dbMatch.scroll_downs) && dbMatch.scroll_downs.length > 0) {
      return dbMatch.scroll_downs.map((sd: any) =>
        typeof sd === "string" ? sd : sd.description
      );
    }
  }

  // 2. Default data lookup strictly for this gender and side
  const genderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;

  // If viewSide is provided, prioritize that side
  if (sideKey && genderData[sideKey]) {
    const sideEntry = Object.values(genderData[sideKey]).find(
      (item) => item.label === stripped || item.label === label
    );
    if (sideEntry) return sideEntry.sublocations;
  }

  // Check front dataset
  const frontEntry = Object.values(genderData.front).find(
    (item) => item.label === stripped || item.label === label
  );
  if (frontEntry) return frontEntry.sublocations;

  // Check back dataset
  const backEntry = Object.values(genderData.back).find(
    (item) => item.label === stripped || item.label === label
  );
  if (backEntry) return backEntry.sublocations;

  // Fallback for Backbone (Spine)
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("backbone") || lowerLabel.includes("spine")) {
    if (genderData.back.backbone) {
      return genderData.back.backbone.sublocations;
    }
  }

  // Fallback for merged Perineum / Perianal Area
  if (lowerLabel.includes("perineum") || lowerLabel.includes("perianal") || lowerLabel.includes("anus")) {
    const perineumKey = gender === "female" ? "female-perineum" : "perineum";
    if (genderData.back[perineumKey]) {
      return genderData.back[perineumKey].sublocations;
    }
  }

  return [];
};

export const locationsToBodyData = (
  locs: LocationWithScrollDowns[],
  locationList?: LocationModal[],
  gender: "male" | "female" = "male"
): BodyHighlighterPart[] => {
  const parts: BodyHighlighterPart[] = [];
  const genderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;

  for (const item of locs) {
    const label = item.description;
    let slug: Slug | undefined;
    let side: "left" | "right" | undefined;
    let bodySide: "front" | "back" | undefined;

    if (label.startsWith("Left ")) side = "left";
    else if (label.startsWith("Right ")) side = "right";

    const stripped = label.replace(/^(Left|Right)\s/, "");

    // 1. Check in locationList for this gender
    if (Array.isArray(locationList) && locationList.length > 0) {
      const dbMatch = locationList.find((l) => {
        if (gender === "female" && (l.gender === "male" || l.muscle_id?.startsWith("male-"))) return false;
        if (gender === "male" && (l.gender === "female" || l.muscle_id?.startsWith("female-"))) return false;
        return l.description === label || (side && l.description === stripped);
      });

      if (dbMatch) {
        if (dbMatch.side) {
          bodySide = dbMatch.side;
        }
        if (dbMatch.muscle_id) {
          let cleanId = dbMatch.muscle_id.replace(/^(male|female)-/, "");
          if (cleanId.startsWith("front-")) {
            bodySide = bodySide || "front";
            cleanId = cleanId.replace(/^front-/, "");
          } else if (cleanId.startsWith("back-")) {
            bodySide = bodySide || "back";
            cleanId = cleanId.replace(/^back-/, "");
          }
          if (cleanId.endsWith("-left")) {
            slug = cleanId.replace(/-left$/, "") as Slug;
            side = "left";
          } else if (cleanId.endsWith("-right")) {
            slug = cleanId.replace(/-right$/, "") as Slug;
            side = "right";
          } else {
            slug = cleanId as Slug;
          }
        }
      }
    }

    // 2. Parse from label using this gender's dataset
    if (!slug || !bodySide) {
      // Check front dataset first
      const frontMatched = Object.entries(genderData.front).find(
        ([_, val]) => val.label === stripped || val.label === label
      );
      if (frontMatched) {
        slug = (slug || frontMatched[0]) as Slug;
        bodySide = bodySide || "front";
      } else {
        // Check back dataset
        const backMatched = Object.entries(genderData.back).find(
          ([_, val]) => val.label === stripped || val.label === label
        );
        if (backMatched) {
          slug = (slug || backMatched[0]) as Slug;
          bodySide = bodySide || "back";
        }
      }
    }

    // Normalize legacy anus/perianal slug or label
    if (slug === ("anus" as Slug)) {
      slug = "perineum";
      bodySide = "back";
    } else if (slug === ("female-anus" as Slug)) {
      slug = "female-perineum";
      bodySide = "back";
    } else if (slug === ("spine" as Slug)) {
      slug = "backbone";
      bodySide = "back";
    }

    if (!slug || !bodySide) {
      const lower = label.toLowerCase();
      if (lower.includes("backbone") || lower.includes("spine")) {
        slug = "backbone";
        bodySide = "back";
      } else if (lower.includes("perineum") || lower.includes("perianal") || lower.includes("anus")) {
        slug = (gender === "female" ? "female-perineum" : "perineum") as Slug;
        bodySide = "back";
      }
    }

    if (slug) {
      if (!bodySide) {
        bodySide = slug in genderData.back && !(slug in genderData.front) ? "back" : "front";
      }
      parts.push({
        slug,
        intensity: 1,
        ...(side && BILATERAL_SLUGS.includes(slug) ? { side } : {}),
        bodySide: bodySide || "front",
      });
    }
  }

  return parts;
};

const SelectLocationModal = ({
  title,
  isOpen,
  onClose,
  onSave,
  selectedLocations = [],
  onToggleLocation,
  context,
  locationList,
  textSize = "medium",
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called with the full selected list when the user presses Save. */
  onSave?: (locations: LocationWithScrollDowns[]) => void;
  selectedLocations?: LocationWithScrollDowns[];
  onToggleLocation?: (val: string, label: string) => void;
  context?: "complaint" | "otherSymptom" | "examination" | "impression" | "assessment";
  locationList?: LocationModal[];
  textSize?: "small" | "medium" | "large";
}) => {
  const modalTitleFontSize = textSize === "small" ? "15px" : textSize === "large" ? "22px" : "18px";
  const subOptionFontSize = textSize === "small" ? "12px" : textSize === "large" ? "16px" : "14px";

  const [gender, setGender] = useState<"male" | "female">("male");
  const [site, setSite] = useState<"Front" | "Back">("Front");
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [bodyData, setBodyData] = useState<BodyHighlighterPart[]>([]);
  const [storedSelectedLocations, setStoredSelectedLocations] = useState<
    LocationWithScrollDowns[]
  >(selectedLocations || []);

  // Sync state with props when modal opens or gender changes
  useEffect(() => {
    if (isOpen) {
      const initialLocs = selectedLocations || [];
      setStoredSelectedLocations(initialLocs);
      setBodyData(locationsToBodyData(initialLocs, locationList, gender));
    }
  }, [isOpen, gender]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBodyPartClick = (bodyPart: ExtendedBodyPart, side?: "left" | "right") => {
    const slug = bodyPart.slug;
    if (!slug) return;

    const isBilateral = BILATERAL_SLUGS.includes(slug);
    const partSide = isBilateral ? side : undefined;
    const currentBodySide = site.toLowerCase() as "front" | "back";
    const label = buildLabel(slug, partSide, locationList, site, gender);

    // Toggle body highlight specifically for this bodySide (front or back)
    setBodyData((prev) => {
      const existingIdx = prev.findIndex(
        (d) =>
          d.slug === slug &&
          (isBilateral ? d.side === partSide : true) &&
          (d.bodySide ? d.bodySide === currentBodySide : true)
      );
      if (existingIdx !== -1) {
        // Deselect
        return prev.filter((_, i) => i !== existingIdx);
      }
      // Select
      const newPart: BodyHighlighterPart = {
        slug,
        intensity: 1,
        ...(partSide ? { side: partSide } : {}),
        bodySide: currentBodySide,
      };
      return [...prev, newPart];
    });

    // Toggle in the locations list
    setStoredSelectedLocations((prevLocs) => {
      const alreadySelected = prevLocs.some((item) => item.description === label);
      if (alreadySelected) {
        return prevLocs.filter((item) => item.description !== label);
      }
      return [...prevLocs, { description: label, scrollDowns: [] }];
    });

    if (onToggleLocation) {
      onToggleLocation(slug, label);
    }
  };

  const handleRemoveLocation = (label: string) => {
    // Find the slug and bodySide for this label so we can de-highlight the body
    const stripped = label.replace(/^(Left|Right)\s/, "");
    const side: "left" | "right" | undefined = label.startsWith("Left ")
      ? "left"
      : label.startsWith("Right ")
        ? "right"
        : undefined;

    const genderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
    let matchedSlug: Slug | undefined;
    let matchedBodySide: "front" | "back" | undefined;

    // Check front dataset first
    const frontMatch = Object.entries(genderData.front).find(
      ([_, val]) => val.label === stripped || val.label === label
    );
    if (frontMatch) {
      matchedSlug = frontMatch[0] as Slug;
      matchedBodySide = "front";
    } else {
      // Check back dataset
      const backMatch = Object.entries(genderData.back).find(
        ([_, val]) => val.label === stripped || val.label === label
      );
      if (backMatch) {
        matchedSlug = backMatch[0] as Slug;
        matchedBodySide = "back";
      }
    }

    if (!matchedSlug && Array.isArray(locationList)) {
      const dbMatch = locationList.find((l) => {
        if (gender === "female" && (l.gender === "male" || l.muscle_id?.startsWith("male-"))) return false;
        if (gender === "male" && (l.gender === "female" || l.muscle_id?.startsWith("female-"))) return false;
        return l.description === label || (side && l.description === stripped);
      });
      if (dbMatch) {
        matchedBodySide = dbMatch.side;
        if (dbMatch.muscle_id) {
          let cleanId = dbMatch.muscle_id.replace(/^(male|female)-/, "");
          if (cleanId.startsWith("front-")) {
            matchedBodySide = matchedBodySide || "front";
            cleanId = cleanId.replace(/^front-/, "");
          } else if (cleanId.startsWith("back-")) {
            matchedBodySide = matchedBodySide || "back";
            cleanId = cleanId.replace(/^back-/, "");
          }
          matchedSlug = cleanId.replace(/-(left|right)$/, "") as Slug;
        }
      }
    }

    if (!matchedSlug) {
      const lower = label.toLowerCase();
      if (lower.includes("backbone") || lower.includes("spine")) {
        matchedSlug = "backbone";
        matchedBodySide = "back";
      } else if (lower.includes("perineum") || lower.includes("perianal") || lower.includes("anus")) {
        matchedSlug = (gender === "female" ? "female-perineum" : "perineum") as Slug;
        matchedBodySide = "back";
      }
    }

    if (matchedSlug) {
      if (matchedSlug === ("anus" as Slug)) matchedSlug = "perineum";
      if (matchedSlug === ("female-anus" as Slug)) matchedSlug = "female-perineum";
      if (matchedSlug === ("spine" as Slug)) matchedSlug = "backbone";
      const isBilateral = BILATERAL_SLUGS.includes(matchedSlug);
      setBodyData((prev) =>
        prev.filter(
          (d) =>
            !(
              d.slug === matchedSlug &&
              (isBilateral ? d.side === side : true) &&
              (!d.bodySide || !matchedBodySide || d.bodySide === matchedBodySide)
            )
        )
      );
    }

    setStoredSelectedLocations((prev) =>
      prev.filter((item) => item.description !== label)
    );
  };

  const toggleSublocation = (locationDescription: string, sublocationName: string) => {
    setStoredSelectedLocations((prev) =>
      prev.map((item) => {
        if (item.description === locationDescription) {
          const exists = item.scrollDowns.includes(sublocationName);
          const updatedScrollDowns = exists
            ? item.scrollDowns.filter((s) => s !== sublocationName)
            : [...item.scrollDowns, sublocationName];
          return { ...item, scrollDowns: updatedScrollDowns };
        }
        return item;
      })
    );
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="shadow-black/80 relative mx-4 flex h-[90vh] w-[90%] max-w-7xl flex-col rounded-2xl bg-surfacePrimary p-8 shadow-xl dark:bg-navy-800">
        <button
          className="absolute right-4 top-3 text-2xl font-bold text-textSecondary hover:text-textPrimary"
          onClick={() => {
            onClose();
          }}
        >
          &times;
        </button>

        <div className="flex h-full flex-col gap-6">
          <h2 style={{ fontSize: modalTitleFontSize }} className="shrink-0 font-semibold text-textPrimary">
            {title}
          </h2>

          <div className="flex min-h-0 w-full flex-1 flex-row items-stretch justify-between gap-6 overflow-hidden">
            {/* Left Side: Options and Sublocations of Selected Body Parts */}
            <div className="no-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto pr-2">
              <h2 style={{ fontSize: modalTitleFontSize }} className="shrink-0 font-semibold text-textPrimary">
                Selected Body Parts &amp; Options
              </h2>

              {storedSelectedLocations.length === 0 ? (
                <div className="bg-surfaceSecondary/40 flex h-full min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed border-borderPrimary p-8 text-center">
                  <LiaSearchLocationSolid className="mb-3 text-5xl text-brand-500 opacity-60" />
                  <h3 className="text-base font-semibold text-textPrimary">
                    No Body Part Selected
                  </h3>
                  <p className="mt-1 max-w-xs text-sm text-textSecondary">
                    Hover and click on any region of the body model on the right
                    to select it and view available sublocation options.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {storedSelectedLocations.map((item, index) => {
                    const stripped = item.description.replace(/^(Left|Right)\s+/, "");
                    const genderData = gender === "female" ? BODY_LOCATIONS_DATA.female : BODY_LOCATIONS_DATA.male;
                    const isBack = Object.values(genderData.back).some(
                      (v) => v.label.toLowerCase() === stripped.toLowerCase() || v.label.toLowerCase() === item.description.toLowerCase()
                    );
                    const itemSide: "Front" | "Back" = isBack ? "Back" : "Front";
                    const sublocations = getSublocationsForLabel(item.description, locationList, gender, itemSide);
                    return (
                      <div
                        key={index}
                        className="bg-surfaceSecondary/50 flex flex-col rounded-xl border border-borderPrimary p-4 transition duration-200 hover:border-brand-500/80"
                      >
                        <div className="border-borderPrimary/60 flex items-center justify-between border-b pb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-brand-500">
                              {item.description}
                            </span>
                            <span className="rounded-md bg-brand-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-brand-500">
                              {itemSide}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveLocation(item.description)}
                            className="rounded-md p-1 text-sm font-semibold text-red-500 transition hover:bg-red-500/10 active:bg-red-500/20"
                            title="Remove body part"
                          >
                            &times; Remove
                          </button>
                        </div>

                        {sublocations.length === 0 ? (
                          <p className="mt-3 text-xs italic text-textSecondary">
                            No sublocations available for this body part.
                          </p>
                        ) : (
                          <div className="mt-3 flex flex-col gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-textSecondary">
                              Select Sublocations:
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {sublocations.map((sub, sIdx) => {
                                const isSubSelected = item.scrollDowns.includes(sub);
                                return (
                                  <button
                                    key={sIdx}
                                    onClick={() => toggleSublocation(item.description, sub)}
                                    style={{ fontSize: subOptionFontSize }}
                                    className={`${isSubSelected
                                      ? "!bg-brand-500 !text-white shadow-sm"
                                      : "bg-surfacePrimary text-brand-500 hover:bg-surfacePrimary/80 active:bg-surfacePrimary/70"
                                      } rounded-lg border border-borderPrimary px-3 py-1.5 font-bold transition duration-200 hover:cursor-pointer`}
                                  >
                                    {sub}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Vertical Divider */}
            <div className="relative block w-[1px] bg-textPrimary opacity-30"></div>

            {/* Right Side: Interactive Body Model */}
            <div className="relative flex flex-1 flex-col items-center">
              <div className="relative flex w-full flex-1 flex-col items-center overflow-y-auto">
                <div className="flex w-full flex-wrap items-center justify-between gap-2 pb-2">
                  <h2 className="shrink-0 text-lg font-semibold text-textPrimary">
                    Body Map ({gender === "female" ? "Female" : "Male"} - {site})
                  </h2>

                  <div className="flex items-center gap-2">
                    {/* Gender Segmented Switch */}
                    <div className="flex items-center rounded-lg border border-borderPrimary bg-surfaceSecondary p-1">
                      <button
                        type="button"
                        onClick={() => setGender("male")}
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition ${gender === "male"
                          ? "bg-brand-500 text-white shadow-sm"
                          : "text-textSecondary hover:text-textPrimary"
                          }`}
                      >
                        <PiGenderMale className="text-sm" />
                        <span>Male</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setGender("female")}
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition ${gender === "female"
                          ? "bg-brand-500 text-white shadow-sm"
                          : "text-textSecondary hover:text-textPrimary"
                          }`}
                      >
                        <PiGenderFemale className="text-sm" />
                        <span>Female</span>
                      </button>
                    </div>

                    {/* View Side Switch */}
                    <button
                      aria-label="Switch Side"
                      onClick={() =>
                        setSite((prev) => (prev === "Front" ? "Back" : "Front"))
                      }
                      className="relative flex items-center justify-center gap-1.5 rounded-lg border border-borderPrimary bg-surfaceSecondary px-3 py-1.5 text-xs font-medium text-textPrimary transition duration-200 hover:cursor-pointer hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70"
                    >
                      <PiCameraRotateLight className="text-sm" />
                      <span>{site === "Front" ? "Back View" : "Front View"}</span>
                    </button>
                  </div>
                </div>

                {/* Hover Label Overlay */}
                {hoveredLabel && (
                  <div className="pointer-events-none absolute top-12 z-20 rounded-full bg-brand-500 px-4 py-1 text-xs font-semibold text-white shadow-md backdrop-blur-sm transition-all">
                    {hoveredLabel}
                  </div>
                )}

                <div className="relative box-border block w-full flex-1">
                  <div className="relative flex h-full items-center justify-center">
                    <div
                      onMouseLeave={() => setHoveredLabel(null)}
                      onMouseOver={(e) => {
                        const target = e.target as SVGPathElement;
                        const id = target.id as Slug;
                        const targetSide = target.getAttribute("data-side") as "left" | "right" | undefined;
                        if (id) {
                          const label = buildLabel(id, targetSide === "left" || targetSide === "right" ? targetSide : undefined, locationList, site, gender);
                          setHoveredLabel(label !== id ? label : null);
                        }
                      }}
                    >
                      <div className="flex pointer-events-none flex-row justify-between items-center absolute top-1/2 left-1/2 w-full box-border -translate-x-1/2 -translate-y-1/2">
                        <p className="text-[150px] font-bold text-textPrimary/10">{site === "Back" ? "L" : "R"}</p>
                        <p className="text-[150px] font-bold text-textPrimary/10">{site === "Back" ? "R" : "L"}</p>
                      </div>
                      <div style={{ transform: site === "Front" ? "scaleX(-1)" : "none" }}>
                        <Body
                          data={bodyData.filter((d) => (d.bodySide || "front") === site.toLowerCase())}
                          side={site === "Front" ? "front" : "back"}
                          gender={gender}
                          scale={1.4}
                          border="#dfdfdf"
                          onBodyPartClick={handleBodyPartClick}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-auto flex w-full shrink-0 justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    onClose();
                  }}
                  className="rounded-lg border border-borderPrimary bg-surfaceSecondary px-6 py-3 text-sm font-semibold text-textSecondary transition duration-200 hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (onSave) onSave(storedSelectedLocations);
                    onClose();
                  }}
                  className="rounded-lg bg-brand-500 px-6 py-3 text-sm font-semibold text-white transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70"
                >
                  Save Selections
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectLocationModal;
